import React, { useState, useEffect } from 'react'

interface TimeNavigationProps {
  verticalViewingAngle: number
  setVerticalViewingAngle: (angle: number) => void
  currentTime: Date
  setCurrentTime: (time: Date) => void
}

const TimeNavigation: React.FC<TimeNavigationProps> = ({
  verticalViewingAngle,
  setVerticalViewingAngle,
  currentTime,
  setCurrentTime
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [playStartTime, setPlayStartTime] = useState<number | null>(null)
  const [pausedTime, setPausedTime] = useState(new Date())
  const [timeMultiplier, setTimeMultiplier] = useState(1)

  // Available multipliers: 0.25x, 0.5x, 1x, 2x, 4x, 8x, 16x, 32x, 64x, 128x
  const multipliers = [0.25, 0.5, 1, 2, 4, 8, 16, 32, 64, 128]

  // Logarithmic scale conversion for vertical viewing angle
  // Maps slider position (0-100) to angle (0.1-90 degrees) logarithmically
  const sliderToAngle = (sliderValue: number): number => {
    const minAngle = 0.1
    const maxAngle = 90
    const minLog = Math.log(minAngle)
    const maxLog = Math.log(maxAngle)
    const scale = (maxLog - minLog) / 100
    return Math.exp(minLog + scale * sliderValue)
  }

  const angleToSlider = (angle: number): number => {
    const minAngle = 0.1
    const maxAngle = 90
    const minLog = Math.log(minAngle)
    const maxLog = Math.log(maxAngle)
    const scale = (maxLog - minLog) / 100
    return (Math.log(angle) - minLog) / scale
  }

  // Track slider position separately from the actual angle
  const [sliderPosition, setSliderPosition] = useState(angleToSlider(verticalViewingAngle))

  // Sync slider position when prop changes externally
  useEffect(() => {
    setSliderPosition(angleToSlider(verticalViewingAngle))
  }, [verticalViewingAngle])

  // Update time every 10ms for smooth decimal seconds, but only when playing
  useEffect(() => {
    if (!isPlaying) return

    const interval = setInterval(() => {
      if (playStartTime !== null) {
        const elapsed = (Date.now() - playStartTime) * timeMultiplier
        setCurrentTime(new Date(pausedTime.getTime() + elapsed))
      }
    }, 10)

    return () => clearInterval(interval)
  }, [isPlaying, playStartTime, pausedTime, timeMultiplier])

  const formatDate = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const formatTime = (date: Date): string => {
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = date.getSeconds()
    const milliseconds = date.getMilliseconds()
    const decimalSeconds = (seconds + milliseconds / 1000).toFixed(2).padStart(5, '0')
    return `${hours}:${minutes}:${decimalSeconds}`
  }

  const handlePlayPause = () => {
    if (isPlaying) {
      // Pausing: save the current time
      setPausedTime(currentTime)
      setIsPlaying(false)
    } else {
      // Playing: start from the paused time
      setPlayStartTime(Date.now())
      setIsPlaying(true)
    }
  }

  const handleBackward = () => {
    // Skip backward by 10 seconds * current multiplier
    const skipAmount = 10000 * timeMultiplier // 10 seconds in milliseconds
    const newTime = new Date(currentTime.getTime() - skipAmount)

    setCurrentTime(newTime)
    setPausedTime(newTime)

    // Reset play start time if playing to prevent jumps
    if (isPlaying) {
      setPlayStartTime(Date.now())
    }
  }

  const handleForward = () => {
    // Skip forward by 10 seconds * current multiplier
    const skipAmount = 10000 * timeMultiplier // 10 seconds in milliseconds
    const newTime = new Date(currentTime.getTime() + skipAmount)

    setCurrentTime(newTime)
    setPausedTime(newTime)

    // Reset play start time if playing to prevent jumps
    if (isPlaying) {
      setPlayStartTime(Date.now())
    }
  }

  const handleMultiplierDecrease = () => {
    const currentIndex = multipliers.indexOf(timeMultiplier)
    if (currentIndex > 0) {
      const newMultiplier = multipliers[currentIndex - 1]
      setTimeMultiplier(newMultiplier)
      // Reset play start time to prevent time jumps
      if (isPlaying) {
        setPausedTime(currentTime)
        setPlayStartTime(Date.now())
      }
    }
  }

  const handleMultiplierIncrease = () => {
    const currentIndex = multipliers.indexOf(timeMultiplier)
    if (currentIndex < multipliers.length - 1) {
      const newMultiplier = multipliers[currentIndex + 1]
      setTimeMultiplier(newMultiplier)
      // Reset play start time to prevent time jumps
      if (isPlaying) {
        setPausedTime(currentTime)
        setPlayStartTime(Date.now())
      }
    }
  }

  return (
    <div className="h-20 bg-gray-800 text-white flex items-center px-4 shadow-lg">
      {/* Three column layout */}
      {/* Left column - Date and time display */}
      <div className="flex-1 flex justify-end items-center pr-6">
        <div className="flex flex-col items-end gap-0.5">
          <div className="font-mono text-xs text-gray-400 tracking-wider">
            {formatDate(currentTime)}
          </div>
          <div className="font-mono text-lg font-semibold text-white tracking-wide">
            {formatTime(currentTime)}
          </div>
        </div>
      </div>

      {/* Center column - Fixed size control group */}
      <div className="flex items-center gap-3">
        {/* Backward button */}
        <button
          onClick={handleBackward}
          className="w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600 active:bg-gray-500
                     flex items-center justify-center transition-colors duration-150
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800"
          aria-label="Step backward"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-6 h-6"
          >
            <path d="M9.195 18.44c1.25.713 2.805-.19 2.805-1.629v-2.34l6.945 3.968c1.25.714 2.805-.188 2.805-1.628V8.688c0-1.44-1.555-2.342-2.805-1.628L12 11.03v-2.34c0-1.44-1.555-2.343-2.805-1.629l-7.108 4.062c-1.26.72-1.26 2.536 0 3.256l7.108 4.061z" />
          </svg>
        </button>

        {/* Play/Pause button */}
        <button
          onClick={handlePlayPause}
          className="w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-500 active:bg-blue-700
                     flex items-center justify-center transition-colors duration-150
                     focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-800
                     shadow-lg"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-7 h-7"
            >
              <path fillRule="evenodd" d="M6.75 5.25a.75.75 0 01.75-.75H9a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H7.5a.75.75 0 01-.75-.75V5.25zm7.5 0A.75.75 0 0115 4.5h1.5a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H15a.75.75 0 01-.75-.75V5.25z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-7 h-7 ml-1"
            >
              <path fillRule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" />
            </svg>
          )}
        </button>

        {/* Forward button */}
        <button
          onClick={handleForward}
          className="w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600 active:bg-gray-500
                     flex items-center justify-center transition-colors duration-150
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800"
          aria-label="Step forward"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-6 h-6"
          >
            <path d="M5.055 7.06c-1.25-.714-2.805.189-2.805 1.628v8.123c0 1.44 1.555 2.342 2.805 1.628L12 14.471v2.34c0 1.447 1.555 2.342 2.805 1.628l7.108-4.061c1.26-.72 1.26-2.536 0-3.256L14.805 7.06C13.555 6.346 12 7.25 12 8.688v2.34L5.055 7.06z" />
          </svg>
        </button>
      </div>

      {/* Right column - Time multiplier controls and zoom slider */}
      <div className="flex-1 flex justify-start items-center pl-6">
        <div className="flex items-center gap-6">
          {/* Time multiplier controls */}
          <div className="flex items-center gap-2">
            {/* Decrease multiplier button */}
            <button
              onClick={handleMultiplierDecrease}
              disabled={timeMultiplier === multipliers[0]}
              className="w-8 h-8 rounded bg-gray-700 hover:bg-gray-600 active:bg-gray-500
                         disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed
                         flex items-center justify-center transition-colors duration-150
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800"
              aria-label="Decrease time multiplier"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path fillRule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Multiplier display */}
            <div className="min-w-[60px] h-9 px-3 rounded bg-gray-700 flex items-center justify-center">
              <span className="font-mono text-sm font-semibold">
                {timeMultiplier >= 1 ? `${timeMultiplier}x` : `${timeMultiplier}x`}
              </span>
            </div>

            {/* Increase multiplier button */}
            <button
              onClick={handleMultiplierIncrease}
              disabled={timeMultiplier === multipliers[multipliers.length - 1]}
              className="w-8 h-8 rounded bg-gray-700 hover:bg-gray-600 active:bg-gray-500
                         disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed
                         flex items-center justify-center transition-colors duration-150
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800"
              aria-label="Increase time multiplier"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path fillRule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {/* Vertical Viewing Angle slider */}
          <div className="flex items-center gap-2">
            <label htmlFor="vertical-angle-slider" className="text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path d="M8.25 10.875a2.625 2.625 0 115.25 0 2.625 2.625 0 01-5.25 0z" />
                <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.125 4.5a4.125 4.125 0 102.338 7.524l2.007 2.006a.75.75 0 101.06-1.06l-2.006-2.007a4.125 4.125 0 00-3.399-6.463z" clipRule="evenodd" />
              </svg>
            </label>
            <input
              id="vertical-angle-slider"
              type="range"
              min="0"
              max="100"
              step="0.1"
              value={sliderPosition}
              onChange={(e) => {
                const newSliderPos = parseFloat(e.target.value)
                setSliderPosition(newSliderPos)
                setVerticalViewingAngle(sliderToAngle(newSliderPos))
              }}
              className="w-32 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800
                         [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                         [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600
                         [&::-webkit-slider-thumb]:hover:bg-blue-500 [&::-webkit-slider-thumb]:active:bg-blue-700
                         [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full
                         [&::-moz-range-thumb]:bg-blue-600 [&::-moz-range-thumb]:border-0
                         [&::-moz-range-thumb]:hover:bg-blue-500 [&::-moz-range-thumb]:active:bg-blue-700"
              aria-label="Vertical viewing angle"
            />
            <div className="min-w-[40px] text-sm text-gray-400 font-mono">
              {verticalViewingAngle.toFixed(1)}°
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TimeNavigation
